import {
    Box,
    Divider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { Ruler, Triangle } from 'lucide-react';
import AnnotationClearAllButton from '~/components/Annotations/AnnotationClearAllButton';
import AnnotationSeriesGroupList from '~/components/toolbar/shared/AnnotationSeriesGroupList';
import { useMeasurementController } from '~/hooks/useMeasurementController';
import { MeasurementKind } from '~/domain/measurement/measurement';
import MeasurementRow from './MeasurementRow';

/**
 * Management surface for the measurement tool: pick the kind (distance /
 * angle), see the list of existing measurements (grouped by image series when
 * more than one image is involved), toggle each one's visibility, delete one,
 * or clear them all, all without leaving the active tool, which is the
 * explicit requirement.
 */
export default function MeasurementPanel() {
    const intl = useIntl();
    const {
        ids,
        groups,
        showSeriesLabels,
        mode,
        setMode,
        remove: removeMeasurement,
        toggleVisibility,
        clearAll,
        setHighlighted,
        isObliqueToImage,
        goTo: goToMeasurement,
    } = useMeasurementController();

    const handleModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        next: MeasurementKind | null
    ) => {
        if (next) {
            setMode(next);
        }
    };

    const hintId =
        mode === 'distance'
            ? 'measurement.hint.distance'
            : 'measurement.hint.angle';

    return (
        <Box data-testid="measurement-panel">
            <ToggleButtonGroup
                value={mode}
                exclusive
                size="small"
                fullWidth
                onChange={handleModeChange}
                sx={{ mb: 1 }}
            >
                <ToggleButton value="distance">
                    <Ruler size={15} style={{ marginRight: 6 }} />
                    {intl.formatMessage({ id: 'measurement.mode.distance' })}
                </ToggleButton>
                <ToggleButton value="angle">
                    <Triangle size={15} style={{ marginRight: 6 }} />
                    {intl.formatMessage({ id: 'measurement.mode.angle' })}
                </ToggleButton>
            </ToggleButtonGroup>

            <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', mb: 1 }}
            >
                {intl.formatMessage({ id: hintId })}
            </Typography>

            <Box sx={{ mb: 1 }}>
                <AnnotationClearAllButton
                    itemCount={ids.length}
                    onClear={clearAll}
                    labelId="measurement.clearAll"
                    confirmLabelId="measurement.clearAll.confirm"
                    variant="outlined"
                    fullWidth
                />
            </Box>

            <Divider sx={{ mb: 1 }} />

            {ids.length === 0 ? (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {intl.formatMessage({ id: 'measurement.empty' })}
                </Typography>
            ) : (
                <Stack spacing={1}>
                    <AnnotationSeriesGroupList
                        groups={groups}
                        showSeriesLabels={showSeriesLabels}
                        renderRow={(id) => (
                            <MeasurementRow
                                id={id}
                                isOblique={isObliqueToImage}
                                onGoTo={goToMeasurement}
                                onToggleVisibility={toggleVisibility}
                                onRemove={removeMeasurement}
                                onHighlight={setHighlighted}
                            />
                        )}
                    />
                </Stack>
            )}
        </Box>
    );
}
